import { Box } from '../../../../../../mads';
import { useScrollYPosition } from '../../../../../../shared/util/scrollPosition';
import { useViewJobDataContext } from '../../../../context/ViewJobDataContext';

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Animated,
    Image,
    type ImageStyle,
    type LayoutChangeEvent,
    type StyleProp,
} from 'react-native';

export type BannerProps = {
    /**
     * The URL of the banner IMG.
     */
    url: string;
    /**
     * The accessibility text of the img for screen readers.
     */
    altText: string;
};

/**
 * Represents the Company Banner image that is present at the top of the page.
 */
export const Banner = (props: BannerProps) => {
    // Measure the parent's layout width via onLayout instead of useWindowDimensions().
    // useWindowDimensions returns the full browser/window width, which over-sizes the
    // banner height when the banner is rendered inside a narrower container (e.g. the
    // right pane of viewjob desktop 2pane), causing the image to render absurdly tall
    // relative to its actual rendered width.
    const [containerWidth, setContainerWidth] = useState(0);
    const [image, setImage] = useState(DEFAULT_IMAGE_DETAILS);
    const scrollYPos = useScrollYPosition();

    // The scroll-driven parallax/fade is meaningful when the banner is pinned at the
    // top while page content scrolls past it (mobile fullscreen, desktop fullscreen).
    // In desktop 2pane embedded the banner scrolls together with the pane content,
    // and the ScrollYPosition provider (StickyButtonsRoot.web.tsx) is wired to
    // window.scrollY — i.e. the outer SERP scroll, not the pane scroll. That means
    // the interpolations read a stale window.scrollY value, fade the banner to
    // opacity 0, and never recover when the user scrolls the pane back to the top.
    // Disable the scroll-tied animation in this case.
    const rnViewJobDisplay =
        useViewJobDataContext().initialData?.rnViewJobDisplay ?? 'MOBILE_STANDALONE';
    const disableScrollAnimation = rnViewJobDisplay === 'DESKTOP_EMBEDDED';

    const imageHeight = Math.floor(containerWidth * calculateImageRatio(image));

    const onLayout = useCallback((event: LayoutChangeEvent) => {
        const nextWidth = event.nativeEvent.layout.width;
        setContainerWidth((current) => (current === nextWidth ? current : nextWidth));
    }, []);

    const bannerStyle = useMemo(() => {
        if (disableScrollAnimation) {
            return {
                width: '100%',
                height: imageHeight,
            } satisfies StyleProp<ImageStyle>;
        }
        return {
            width: '100%',
            height: imageHeight,
            opacity: scrollYPos.interpolate({
                inputRange: [0, 2 * imageHeight],
                outputRange: [1, 0],
            }),
            transform: [
                {
                    scale: scrollYPos.interpolate({
                        inputRange: [-imageHeight, 0, imageHeight],
                        outputRange: [2, 1, 1],
                    }),
                },
                {
                    translateY: scrollYPos.interpolate({
                        inputRange: [-imageHeight, 0, imageHeight],
                        outputRange: [-imageHeight * 0.25, 0, imageHeight * 0.2],
                    }),
                },
            ],
        } satisfies StyleProp<ImageStyle>;
    }, [imageHeight, scrollYPos, disableScrollAnimation]);

    useEffect(() => {
        const abortController = new AbortController();
        Image.getSize(props.url, (srcImageWidth, srcImageHeight) => {
            if (!abortController.signal.aborted) {
                setImage({
                    height: srcImageHeight,
                    width: srcImageWidth,
                });
            }
        });

        return () => {
            abortController.abort();
        };
    }, [props.url]);

    // The wrapping Box is required so we can measure parent width via onLayout
    // before the Animated.Image mounts. Avoid mounting Animated.Image with
    // imageHeight=0 (happens before the first onLayout fires, during SSR, etc.)
    // because RNW's AnimatedProps freezes _inputStyle on first mount, so a zero
    // height would never recover — the banner would be permanently invisible
    // with a broken scale(2) transform.
    return (
        <Box sx={styles.measureWrapper} onLayout={onLayout} testID={'company-banner-wrapper'}>
            {imageHeight > 0 && (
                <Animated.Image
                    testID={'company-banner'}
                    source={{ uri: props.url }}
                    style={bannerStyle}
                    resizeMode={'cover'}
                    accessibilityLabel={props.altText}
                />
            )}
        </Box>
    );
};

const styles = {
    measureWrapper: { width: '100%' as const },
};

type ImageDetails = {
    height: number;
    width: number;
};

// the banners seem to default to be 1960 x 400 so default to this ratio. If this is not the correct ratio
// there will be a slight flash as it switches the ratio. During that incorrect period it will not stretch the
// image though
export const DEFAULT_IMAGE_DETAILS: ImageDetails = {
    height: 400,
    width: 1960,
};

export const calculateImageRatio = (image: ImageDetails): number => {
    return image.height / image.width;
};
